import {
  Children,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
  type FormHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Badge } from './Badge.js';
import { cardClasses } from './Card.js';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';
import { linkTextClasses } from './link.js';

/**
 * A casca de todo formulário da casa — os blocos, a grade que decide a largura
 * de cada campo, e o rodapé que fica FORA deles.
 *
 * ## Por que ela nasce
 *
 * Medido em 18/09/2026: **37 telas abrem um `<form>`** e nenhuma delas pergunta
 * ao pacote como um formulário se desenha. Cada uma escreve a própria grade —
 * `grid gap-3 sm:grid-cols-2`, `grid gap-4 sm:grid-cols-2`,
 * `grid gap-4 lg:grid-cols-2`, `grid gap-5 sm:grid-cols-2` — em **30 lugares**,
 * com quatro respiros diferentes e três pontos de quebra. É a mesma doença que
 * a medição de 01/09 achou nas larguras de conteúdo e que o `Card` acabou de
 * matar em sete cópias de casca: cada tela isolada está certa, e o conjunto
 * não.
 *
 * E o `sm:grid-cols-2` não é só divergência de respiro — ele é uma decisão
 * **tomada por omissão**. Duas colunas iguais é o que o Tailwind torna barato,
 * não o que o formulário pede: "Nome completo" e "CPF" têm a mesma largura hoje
 * porque ninguém escolheu, e não porque um nome caiba em 25%.
 *
 * ## A primeira versão pôs tudo num bloco só, e isso era o defeito
 *
 * Ela nasceu como um `FormCard`: uma caixa, um cabeçalho, todos os campos e as
 * ações lá dentro. Serve um formulário de três campos e some num de quinze —
 * quem preenche não tem onde parar os olhos, não sabe quanto falta, e o botão
 * "Salvar" pertence visualmente ao último assunto do formulário em vez de ao
 * formulário inteiro.
 *
 * O desenho da segunda leitura de 18/09 separa as três coisas, e a separação é
 * o componente:
 *
 * - **`Form`** é o `<form>` e mais nada — a pilha dos blocos, com 20px entre
 *   eles. Ele conta os blocos e conta as marcas de obrigatório (adiante).
 * - **`FormSection`** é um bloco: um assunto, com cabeçalho próprio numerado e
 *   traço embaixo dele.
 * - **`FormFooter`** é o rodapé, e ele **não pertence ao último bloco**. Fica
 *   depois do cartão, separado por um traço que atravessa a largura inteira,
 *   com o aviso de obrigatórios à esquerda e as ações à direita.
 *
 * Quantos blocos, e se há blocos, é do tamanho e do contexto do formulário: um
 * de três campos é uma seção só, e aí a numeração **não** aparece — um "1." sem
 * um "2." em lugar nenhum é a tela afirmando uma sequência que não existe, que é
 * o mesmo defeito do selo de etapa num formulário de uma etapa.
 *
 * ## A grade é de doze, e as fileiras são proporções
 *
 * `cols={[6, 3, 3]}` é "metade, um quarto, um quarto". O desenho de 18/09 pede
 * quatro fileiras e as quatro batem redondo em doze: 50/25/25 é `[6, 3, 3]`,
 * 33/42/25 é `[4, 5, 3]`, 33/33/34 é `[4, 4, 4]`. Doze é o menor número que
 * divide por 2, 3 e 4 — é por isso que ele é a grade, e não dez ou dezesseis.
 *
 * Abaixo de `sm` não existe fileira: tudo empilha em uma coluna. Três campos
 * lado a lado num telefone dão 100px cada, e um CEP não cabe em 100px.
 *
 * ## `children`, e não uma lista fechada de tipos
 *
 * É a escolha oposta à do `Filters`, e o motivo é a diferença entre as duas
 * peças. Um filtro é sempre o mesmo gesto — escolher e aplicar — e por isso a
 * lista fechada de lá fecha uma porta sem custo. Um campo de formulário carrega
 * máscara, consulta de CEP que preenche três campos vizinhos, valor que depende
 * do que se escolheu acima: uma lista fechada empurraria tudo isso para dentro
 * do componente, ou abriria o `custom: ReactNode` que o docblock do `Filters`
 * recusa por escrito.
 *
 * O que a lista fechada compraria — ninguém desenhar campo à mão — é comprado
 * aqui por outro caminho: `Field`, `Select`, `TextArea` e `MultiSelect` são as
 * únicas peças que desenham controle, e a régua do design é quem cobra a grade
 * escrita à mão.
 */

/** Quantas das doze colunas um campo ocupa. */
export type FormSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

/*
 * O mapa é escrito por extenso porque o Tailwind lê o FONTE, não o que roda:
 * `sm:col-span-${n}` não emite regra nenhuma, e a fileira nasceria sem largura
 * — todos os campos em coluna cheia, em silêncio e só acima de `sm`.
 */
const SPANS: Record<FormSpan, string> = {
  1: 'sm:col-span-1',
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
  4: 'sm:col-span-4',
  5: 'sm:col-span-5',
  6: 'sm:col-span-6',
  7: 'sm:col-span-7',
  8: 'sm:col-span-8',
  9: 'sm:col-span-9',
  10: 'sm:col-span-10',
  11: 'sm:col-span-11',
  12: 'sm:col-span-12',
};

/**
 * Uma fileira: as proporções, e os campos na mesma ordem.
 *
 * ## Contagem trocada derruba, e derruba sempre
 *
 * `cols` com três larguras e quatro campos é um layout que parece certo numa
 * largura de janela e erra em outra — e ninguém confere largura de campo numa
 * revisão. É o tipo de divergência que este arquivo inteiro existe para acabar,
 * então ela não pode entrar por aqui.
 *
 * O erro é **determinístico**: não depende de dado, de permissão nem de
 * resposta de API. Quem abrir a tela uma vez o encontra — em desenvolvimento,
 * no `make e2e` ou na primeira renderização —, e por isso ele não chega a
 * produção como um `throw` dependente de dado chegaria. É o mesmo argumento do
 * `auditRoutes`, que derruba a partida em vez de servir uma rota sem guarda.
 */
export function FormRow({
  cols,
  children,
  className,
}: {
  /** Uma largura por campo, em doze avos. A soma não passa de 12. */
  cols: readonly FormSpan[];
  children: ReactNode;
  className?: string;
}) {
  /*
   * `toArray` descarta `null`, `undefined` e booleano — que é exatamente o que
   * um campo condicional (`podeVer && <Field/>`) vira quando não aparece. A
   * contagem conferida é a dos campos DESENHADOS, e é o que `cols` descreve.
   */
  const fields = Children.toArray(children);

  if (fields.length !== cols.length) {
    throw new Error(
      `FormRow: ${cols.length} largura(s) em \`cols\` para ${fields.length} campo(s). ` +
        'Uma largura por campo, na mesma ordem — campo escondido por permissão não conta.',
    );
  }

  const total = cols.reduce((sum, span) => sum + span, 0);
  if (total > 12) {
    throw new Error(
      `FormRow: as larguras somam ${total}, e a grade é de 12. ` +
        'Metade é 6, um terço é 4, um quarto é 3.',
    );
  }

  return (
    /*
     * 14px entre colunas e 12px entre linhas — as medidas do desenho de 18/09,
     * com uma diferença declarada: lá o respiro vertical é 10px, e lá nenhum
     * campo tem frase de ajuda embaixo. Aqui quase todos têm, e 10px encostam a
     * ajuda de um campo no rótulo do seguinte.
     */
    <div className={cn('grid grid-cols-1 gap-x-3.5 gap-y-3 sm:grid-cols-12', className)}>
      {fields.map((field, index) => (
        <div key={index} className={cn('min-w-0', SPANS[cols[index]!])}>
          {field}
        </div>
      ))}
    </div>
  );
}

/**
 * O traço entre dois assuntos DENTRO do mesmo bloco.
 *
 * Ele existe para o bloco que tem dois grupos pequenos demais para virarem dois
 * `FormSection` — e a pergunta a fazer antes de usá-lo é essa: se os dois lados
 * merecem nome, eles merecem bloco. Um formulário dividido por traços é o que a
 * seção veio substituir.
 *
 * `borda-suave` e não `borda`: ele separa DENTRO de uma caixa que já tem
 * contorno, e a borda de contorno repetida aqui desenharia um segundo cartão lá
 * dentro. É a mesma escolha que o cabeçalho do `Card` faz, com o mesmo motivo.
 */
export function FormDivider({ className }: { className?: string }) {
  return <hr className={cn('h-px border-0 bg-borda-suave', className)} />;
}

/* ------------------------------------------------------------------------- *
 * O que o formulário DERIVA — e por isso ninguém escreve à mão
 * ------------------------------------------------------------------------- */

/**
 * Onde este bloco está na pilha. Nulo para a seção que vive fora de um `Form`.
 *
 * `id` só existe no modo `steps`: é a âncora que o trilho usa para levar até
 * a seção.
 */
const SectionPosition = createContext<{
  index: number;
  total: number;
  id?: string;
} | null>(null);

/**
 * O que o modo `steps` divide com as peças de dentro. Nulo fora dele — e é
 * isso que mantém o cabeçalho e o rodapé de todo outro formulário intactos.
 */
interface StepsState {
  /** Já se tentou enviar. A partir daí, etapa incompleta é erro, e não "ainda não". */
  attempted: boolean;
  /** Obrigatórios que faltam, somados de todas as seções. */
  remaining: number;
  /** Leva à primeira seção incompleta e põe o foco nela. */
  goToNext: () => void;
  /**
   * Há trilho — duas seções ou mais. Sem trilho não há rodapé fixo: um
   * formulário de um bloco cabe na tela, e um rodapé colado embaixo dele,
   * dentro de outro formulário, cobriria o formulário de fora.
   */
  rail: boolean;
}
const Steps = createContext<StepsState | null>(null);

/** Como um controle avisa o formulário de que desenhou um `*`. */
const RequiredMarks = createContext<((on: boolean) => void) | null>(null);

/** Se algum controle desenhou um `*`. É o que decide o aviso do rodapé. */
const HasRequired = createContext(false);

/**
 * O número na frente do título — `3.` em "3. Documentação e anexos".
 *
 * É a ÚNICA marca de posição do cabeçalho. Houve um contador ao lado do
 * `validacao` — `Bloco 03/04` —, e ele dizia duas vezes o que o número já diz:
 * o canto direito do cabeçalho é onde mora o estado do preenchimento, e um
 * segundo número ali disputa com ele sem acrescentar nada.
 *
 * Sai da posição, nunca do texto do título. Escrito à mão ele sobrevive a um
 * bloco removido do meio, e aí a tela numera 1, 2, 4 — ninguém confere numeração
 * numa revisão, e o defeito só aparece para quem preenche.
 *
 * **Nulo em formulário de um bloco só**, pelo mesmo motivo que tirou o selo de
 * etapa de quem não tem etapa dois: um "1." sem um "2." em lugar nenhum promete
 * uma sequência que não existe.
 */
export function sectionOrdinal(index: number, total: number): string | null {
  return total < 2 ? null : `${index}.`;
}

/**
 * Chamado por todo controle que desenha o `*` do obrigatório.
 *
 * O rodapé explica a marca — "campos marcados com * são obrigatórios" —, e essa
 * frase precisa ser verdade na tela em que ela aparece. Escrita por quem monta o
 * formulário, ela sobrevive ao dia em que o último campo obrigatório vira
 * opcional; contada daqui, ela some sozinha.
 *
 * Fora de um `Form` o contexto é nulo e o gancho não faz nada: o mesmo `Field`
 * serve a barra de filtros e à linha de tabela, e nenhuma das duas tem rodapé.
 */
export function useRequiredMark(required: boolean | undefined): void {
  const announce = useContext(RequiredMarks);

  useEffect(() => {
    if (!announce || !required) return;
    announce(true);
    return () => announce(false);
  }, [announce, required]);
}

/* ------------------------------------------------------------------------- *
 * As três peças
 * ------------------------------------------------------------------------- */

/**
 * O quanto da seção está preenchido — sempre derivado do estado real dos
 * campos, por quem os conhece.
 *
 * O `Form` não conta campo pelo DOM: um `Choice`, a busca do veículo no SGA e
 * o endereço pelo CEP guardam "preenchido" cada um de um jeito, e uma
 * contagem genérica erraria justamente nos campos que mais importam. Quem monta
 * a tela já sabe o que falta — é a mesma lista que decide se o envio passa.
 */
export interface FormSectionStatus {
  /** Obrigatórios preenchidos. */
  done: number;
  /** Obrigatórios da seção. */
  total: number;
  /** O nome de cada obrigatório que falta, na voz da tela: "o protocolo OPA". */
  missing?: readonly string[];
}

/** "a, b e c" — a lista do que falta, como se diz. */
function listOf(items: readonly string[]): string {
  return items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} e ${items.at(-1)}`;
}

function isComplete(status: FormSectionStatus | undefined): boolean {
  return status === undefined || status.done >= status.total;
}

export interface FormSectionProps {
  /** O nome do bloco, em versalete. É o `rotulo` do `theme.css`. */
  title: ReactNode;
  /** A frase abaixo do título: o que este bloco pergunta, e por quê. */
  descricao?: ReactNode;
  /**
   * O canto direito do cabeçalho — quanto já foi preenchido, o que falta.
   *
   * Ele sai do estado REAL dos campos, sempre. "3 de 8" escrito à mão é a tela
   * afirmando o que o código não cumpre, que é o defeito que o `PRODUCT.md`
   * chama de desonestidade de interface.
   */
  validacao?: ReactNode;
  /** O nível do título. A ordem dos cabeçalhos da página é de quem monta a página. */
  level?: 2 | 3;
  /**
   * O ícone do assunto. Só aparece no modo `steps` do `Form`: é ele que deixa
   * sete blocos varríveis sem ler título por título.
   */
  icon?: IconName;
  /** O preenchimento da seção. No modo `steps`, alimenta o selo e o trilho. */
  status?: FormSectionStatus;
  children?: ReactNode;
  className?: string;
}

/**
 * Um bloco do formulário: um assunto, com cabeçalho próprio.
 *
 * A casca vem de `cardClasses`, e não de classes escritas aqui: o bloco de
 * formulário é um cartão, e a oitava cópia da casca seria o defeito que a régua
 * `cartao-a-mao` derruba — cometido dentro do pacote, onde a régua não alcança.
 *
 * ## O cabeçalho não é o do `Card`, e a diferença é o que ele pergunta
 *
 * O do `Card` nomeia um bloco de leitura: título em 13px peso 700, descrição, e
 * ações do lado. Este nomeia um bloco de ESCRITA, e o desenho de 18/09 o põe em
 * versalete de 11px — um degrau abaixo dos rótulos dos campos, que são o que a
 * pessoa lê de verdade. O título de um formulário competindo com os rótulos
 * dele é hierarquia ao contrário.
 *
 * E ele tem **traço embaixo**, que o `Card` só desenha em `sangria`. Aqui o
 * traço não é decoração: ele é o que faz uma pilha de quatro blocos ler como
 * quatro assuntos em vez de como uma folha comprida com quatro frases em
 * versalete no meio.
 *
 * ## A barra à esquerda do título
 *
 * 5px de largura, cor primária. É a única cor do cabeçalho, e ela existe porque
 * o versalete de 11px sozinho não segura o começo do bloco quando há quatro
 * deles na mesma rolagem.
 *
 * A curvatura é `pilula` e não os 2px do desenho: numa barra de 5px de largura
 * a pílula arredonda 2,5px, e a diferença para o desenho é meio pixel de curva.
 * Um `rounded-[2px]` traria um valor de forma que não é token — e token que se
 * escreve à mão é o que este pacote inteiro existe para acabar.
 */
export function FormSection({
  title,
  descricao,
  validacao,
  level = 2,
  icon,
  status,
  children,
  className,
}: FormSectionProps) {
  const position = useContext(SectionPosition);
  const steps = useContext(Steps);
  const Title = level === 2 ? 'h2' : 'h3';

  if (steps && position) {
    return (
      <SteppedSection
        {...{ title, descricao, icon, status, children, className, Title, position }}
        attempted={steps.attempted}
      />
    );
  }

  const ordinal = position && sectionOrdinal(position.index, position.total);

  return (
    <section className={cardClasses({ padding: false, className })}>
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-borda-suave px-4 pb-3 pt-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden="true"
            className="h-3.5 w-[5px] shrink-0 rounded-pilula bg-primaria"
          />
          <Title className="rotulo min-w-0 truncate font-bold text-texto">
            {ordinal && <span className="tabular-nums">{ordinal} </span>}
            {title}
          </Title>
        </div>

        {validacao && (
          /*
           * Mono, e nos 11px do `rotulo` — o desenho pede 9px, e 9px não é
           * degrau desta escala: o menor tamanho da casa é o versalete, e abrir
           * um menor para um canto de cabeçalho seria abrir um menor para tudo.
           * É a mesma decisão de 10/09, que desceu os pedidos de peso 700 em
           * mono para 600 em vez de trazer uma quarta face.
           */
          <p className="shrink-0 font-mono text-rotulo tabular-nums text-texto-suave">
            {validacao}
          </p>
        )}
      </header>

      {descricao && <p className="px-4 pt-3 text-xs text-texto-suave">{descricao}</p>}

      <div className={cn('flex flex-col gap-3 px-4 pb-4', descricao ? 'pt-3' : 'pt-4')}>
        {children}
      </div>
    </section>
  );
}

/**
 * O cabeçalho de ETAPA: o que o formulário longo pede e o curto não.
 *
 * Nasceu em 25/09/2026 para o sinistro — sete blocos e vinte obrigatórios, onde
 * o versalete de 11px com o número na frente não segurava ninguém: quem
 * preenchia rolava sem saber em que assunto estava nem quanto faltava.
 *
 * - O **ícone** num quadro de 40px é o que se acha de relance. Verde quando a
 *   seção fecha — o verde é estado (decisão 1 do `DESIGN.md`), e aqui ele diz
 *   exatamente "está certo".
 * - **"Etapa 2 de 7"** é a posição, em mono; o título sobe para 14px peso 700,
 *   porque aqui ele é a coisa que se procura, e não um rótulo acima de rótulos.
 * - O **selo** diz o preenchimento: "2 de 5 obrigatórios", "Completo", ou —
 *   depois de tentar enviar — "Faltam 2", com a lista do que falta logo abaixo.
 *
 * O destaque da seção em uso é a borda INTEIRA, e só com o foco dentro. Borda
 * lateral colorida é o que o `DESIGN.md` recusa por nome.
 */
function SteppedSection({
  title,
  descricao,
  icon,
  status,
  children,
  className,
  Title,
  position,
  attempted,
}: Pick<
  FormSectionProps,
  'title' | 'descricao' | 'icon' | 'status' | 'children' | 'className'
> & {
  Title: 'h2' | 'h3';
  position: { index: number; total: number; id?: string };
  attempted: boolean;
}) {
  const complete = status !== undefined && isComplete(status);
  const failing = attempted && status !== undefined && !complete;
  const missing = status?.missing ?? [];
  const left = status ? status.total - status.done : 0;

  return (
    <section
      id={position.id}
      className={cardClasses({
        padding: false,
        className: cn(
          /* A faixa do telefone e a do topo não podem cobrir o título ao pular. */
          'scroll-mt-36 transition-colors focus-within:border-primaria-texto lg:scroll-mt-4',
          failing && 'border-erro',
          className,
        ),
      })}
    >
      <header className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 border-b border-borda-suave px-4 py-3.5 sm:grid-cols-[auto_minmax(0,1fr)_auto]">
        <span
          aria-hidden="true"
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-borda transition-colors',
            complete
              ? 'bg-sucesso-suave text-sucesso'
              : 'bg-primaria-suave text-primaria-texto',
          )}
        >
          <Icon nome={complete ? 'confirmado' : (icon ?? 'documento')} size={20} />
        </span>

        <div className="min-w-0">
          {/* "Etapa 1 de 1" promete uma sequência que não existe — o mesmo
              motivo de `sectionOrdinal` não numerar formulário de um bloco. */}
          {position.total > 1 && (
            <p className="font-mono text-rotulo tabular-nums text-texto-tenue">
              Etapa {position.index} de {position.total}
            </p>
          )}
          <Title className="text-sm font-bold text-texto">{title}</Title>
        </div>

        {status && (
          <div className="col-span-2 sm:col-span-1 sm:justify-self-end">
            {/* Seção sem obrigatório não está "completa" — ela não pede nada.
                Com "Completo", a etapa dos arquivos do aviso afirmaria que há
                arquivo quando não há nenhum. */}
            {status.total === 0 ? (
              <Badge tone="neutro">Opcional</Badge>
            ) : complete ? (
              <Badge tone="sucesso">Completo</Badge>
            ) : failing ? (
              <Badge tone="erro">
                Falta{left > 1 ? 'm' : ''} {left}
              </Badge>
            ) : (
              <Badge tone="neutro">
                <span className="tabular-nums">
                  {status.done} de {status.total}
                </span>{' '}
                obrigatório{status.total > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        )}
      </header>

      {descricao && <p className="px-4 pt-3 text-xs text-texto-suave">{descricao}</p>}

      {failing && missing.length > 0 && (
        <p className="flex items-start gap-1.5 px-4 pt-3 text-xs font-medium text-erro">
          <Icon nome="atencao" size={16} className="mt-px shrink-0" />
          <span>Falta {listOf(missing)}.</span>
        </p>
      )}

      <div className="flex flex-col gap-3 px-4 pb-4 pt-3">{children}</div>
    </section>
  );
}

interface StepItem {
  id: string;
  title: ReactNode;
  status?: FormSectionStatus;
}

type StepState = 'feito' | 'erro' | 'aberto';

/**
 * O trilho das etapas — gerado das seções, nunca escrito à mão.
 *
 * Acima de `lg` é uma coluna fixa ao lado do formulário: número, nome e
 * situação de cada seção, com a que está na tela acesa. Abaixo, 216px não
 * sobram, e ele vira a faixa fixa do topo — a etapa atual por extenso e um
 * traço por etapa. O topo dela é `--form-steps-top`, que na página vale
 * `4.25rem` — a faixa do `Header` (60px) mais o respiro — e dentro do `Modal`
 * vale zero, porque lá quem rola é o corpo do diálogo. Acima de `lg` quem rola
 * é o `<main>`, e o topo dele é o zero.
 *
 * "Na tela" é quem cruza a faixa entre 20% e 30% da altura da janela
 * (`IntersectionObserver`), e não quem aparece primeiro: uma seção curta que
 * mal entrou por baixo não rouba o destaque da que se está lendo.
 *
 * Os itens são BOTÕES e não âncoras: um `href="#…"` passaria pelo roteador e
 * escreveria o hash na URL da tela.
 */
function StepsRail({
  items,
  attempted,
}: {
  items: readonly StepItem[];
  attempted: boolean;
}) {
  const [current, setCurrent] = useState(0);
  const ids = items.map((i) => i.id).join(' ');

  useEffect(() => {
    const sections = ids
      .split(' ')
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting)
            setCurrent(sections.indexOf(entry.target as HTMLElement));
        }
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [ids]);

  const done = items.reduce((sum, i) => sum + (i.status?.done ?? 0), 0);
  const total = items.reduce((sum, i) => sum + (i.status?.total ?? 0), 0);
  const stateOf = (item: StepItem): StepState =>
    isComplete(item.status) ? 'feito' : attempted ? 'erro' : 'aberto';
  const active = items[current];

  return (
    <>
      <nav
        aria-label="Etapas do formulário"
        className="sticky top-0 hidden rounded-cartao border border-borda bg-superficie p-2 lg:block"
      >
        <div className="flex flex-col gap-1.5 px-2 pb-3 pt-1.5">
          <p className="flex justify-between gap-2 text-xs text-texto-suave">
            <span>Obrigatórios</span>
            <span className="font-mono font-semibold tabular-nums text-texto">
              {done} de {total}
            </span>
          </p>
          <progress
            value={done}
            max={Math.max(total, 1)}
            aria-label="Obrigatórios preenchidos"
            className="h-1.5 w-full appearance-none overflow-hidden rounded-pilula bg-borda [&::-moz-progress-bar]:bg-primaria [&::-webkit-progress-bar]:bg-borda [&::-webkit-progress-value]:rounded-pilula [&::-webkit-progress-value]:bg-primaria"
          />
        </div>

        <ol className="flex flex-col">
          {items.map((item, index) => {
            const state = stateOf(item);
            const isCurrent = index === current;
            const left = (item.status?.total ?? 0) - (item.status?.done ?? 0);
            return (
              <li key={item.id} className="relative">
                {index < items.length - 1 && (
                  /* O fio entre os números: é ele que desenha a SEQUÊNCIA. */
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute -bottom-1.5 left-[1.4375rem] top-9 w-px',
                      state === 'feito' ? 'bg-sucesso' : 'bg-borda',
                    )}
                  />
                )}
                <button
                  type="button"
                  aria-current={isCurrent ? 'step' : undefined}
                  onClick={() => goTo(item.id)}
                  className={cn(
                    'grid min-h-11 w-full grid-cols-[1.75rem_minmax(0,1fr)] items-center gap-2.5 rounded-borda px-2 py-1.5 text-left transition-colors',
                    'hover:bg-superficie-suave active:bg-borda',
                    isCurrent && 'bg-primaria-suave hover:bg-primaria-suave',
                  )}
                >
                  <StepNumber index={index + 1} state={state} current={isCurrent} />
                  <span className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate text-xs font-semibold text-texto">
                      {item.title}
                    </span>
                    <span
                      className={cn(
                        'text-rotulo',
                        state === 'erro' ? 'font-semibold text-erro' : 'text-texto-suave',
                      )}
                    >
                      {item.status?.total === 0
                        ? 'Opcional'
                        : state === 'feito'
                          ? 'Completo'
                          : state === 'erro'
                            ? `Falta${left > 1 ? 'm' : ''} ${left}`
                            : `${item.status?.done ?? 0} de ${item.status?.total ?? 0}`}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      {active && (
        <nav
          aria-label="Etapa atual"
          className="sticky top-[var(--form-steps-top,4.25rem)] z-10 flex flex-col gap-2 rounded-cartao border border-borda bg-superficie px-3 py-2.5 shadow-flutuante lg:hidden"
        >
          <p className="flex items-center gap-2.5">
            <StepNumber index={current + 1} state={stateOf(active)} current />
            <span className="min-w-0 flex-1 truncate text-sm font-bold text-texto">
              {active.title}
            </span>
            <span className="shrink-0 font-mono text-rotulo tabular-nums text-texto-suave">
              {current + 1}/{items.length} · {done}/{total}
            </span>
          </p>
          <span aria-hidden="true" className="flex gap-1">
            {items.map((item, index) => {
              const state = stateOf(item);
              return (
                <span
                  key={item.id}
                  className={cn(
                    'h-1 flex-1 rounded-pilula',
                    state === 'feito'
                      ? 'bg-sucesso'
                      : state === 'erro'
                        ? 'bg-erro'
                        : index === current
                          ? 'bg-primaria'
                          : 'bg-borda',
                  )}
                />
              );
            })}
          </span>
        </nav>
      )}
    </>
  );
}

/** O número da etapa: vira visto quando fecha, `!` quando falhou no envio. */
function StepNumber({
  index,
  state,
  current,
}: {
  index: number;
  state: StepState;
  current: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative z-[1] flex h-7 w-7 shrink-0 items-center justify-center rounded-pilula border-[1.5px] font-mono text-xs font-semibold tabular-nums',
        state === 'feito'
          ? 'border-sucesso bg-sucesso-suave text-sucesso'
          : state === 'erro'
            ? 'border-erro bg-erro-suave text-erro'
            : current
              ? 'border-primaria-texto bg-primaria text-primaria-contraste'
              : 'border-borda-forte bg-superficie text-texto-suave',
      )}
    >
      {state === 'feito' ? (
        <Icon nome="confirmado" size={16} />
      ) : state === 'erro' ? (
        '!'
      ) : (
        index
      )}
    </span>
  );
}

/** Leva até a seção e põe o foco no primeiro controle vazio dela. */
function goTo(id: string) {
  const section = document.getElementById(id);
  section?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  firstControl(section)?.focus({ preventScroll: true });
}

/** O primeiro controle de uma seção — de preferência o primeiro vazio. */
function firstControl(section: HTMLElement | null): HTMLElement | null {
  if (!section) return null;
  const controls = [
    ...section.querySelectorAll<HTMLInputElement>(
      'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
    ),
  ];
  return controls.find((c) => c.value === '') ?? controls[0] ?? null;
}

/**
 * O rodapé do formulário — e ele fica **fora** do último bloco.
 *
 * Não é preciosismo de desenho: as ações valem para o formulário inteiro, e
 * dentro do último cartão elas viram as ações do último assunto. Num formulário
 * de quatro blocos, "Salvar" desenhado dentro de "Documentação e anexos" parece
 * salvar os anexos.
 *
 * O traço de cima atravessa a largura toda, por isso — ele fecha a pilha, e não
 * uma caixa.
 *
 * ## O aviso de obrigatórios é CONTADO, não escrito
 *
 * A frase só aparece quando algum controle do formulário desenhou um `*` de
 * verdade (`useRequiredMark`). Escrita à mão, ela fica na tela no dia em que o
 * último campo obrigatório vira opcional, e a tela passa a explicar uma marca
 * que não está em lugar nenhum.
 *
 * O `*` do aviso usa a MESMA cor do `*` dos campos. Explicar uma marca com uma
 * marca de outra cor é pedir que a pessoa faça a ligação sozinha.
 *
 * `children` são os botões, na ordem em que se lê: o que desiste primeiro, o que
 * conclui por último — o `type="submit"` é o da direita.
 */
export function FormFooter({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  const hasRequired = useContext(HasRequired);
  const steps = useContext(Steps);

  if (steps) {
    /*
     * No modo `steps` o rodapé FICA na tela: com sete blocos, o botão no fim da
     * página é o que ninguém vê enquanto preenche o bloco 3. E no lugar da
     * explicação do `*` ele diz o que importa agora — quanto falta, e um atalho
     * para o próximo campo vazio.
     */
    const { remaining, attempted, goToNext, rail } = steps;
    const footer = (
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-x-4 gap-y-3',
          rail
            ? 'rounded-cartao border border-borda bg-superficie px-4 py-3 shadow-flutuante'
            : 'border-t border-borda pt-3.5',
          className,
        )}
      >
        <p
          aria-live="polite"
          className={cn(
            'flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs',
            remaining === 0
              ? 'text-sucesso'
              : attempted
                ? 'text-erro'
                : 'text-texto-suave',
          )}
        >
          <Icon
            nome={remaining === 0 ? 'confirmado' : attempted ? 'atencao' : 'informacao'}
            size={16}
            className="shrink-0"
          />
          {remaining === 0 ? (
            <span className="font-semibold">Os obrigatórios estão preenchidos.</span>
          ) : (
            <>
              <span>
                <span className="font-semibold tabular-nums">
                  Falta{remaining > 1 ? 'm' : ''} {remaining} obrigatório
                  {remaining > 1 ? 's' : ''}
                </span>{' '}
                para enviar.
              </span>
              <button
                type="button"
                onClick={goToNext}
                className={linkTextClasses('font-semibold underline underline-offset-2')}
              >
                Ir para o próximo
              </button>
            </>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">{children}</div>
      </div>
    );
    if (!rail) return footer;
    /*
     * A barra fixa precisa de uma FAIXA em volta, opaca e da cor do fundo de
     * quem rola: sem ela o formulário passa por trás da barra e aparece nas
     * bordas dela — o rótulo do campo seguinte embaixo, a borda do cartão da
     * seção encostada em cima —, e as caixas parecem trombar.
     *
     * Em cima, `pt-3` de respiro. Embaixo, a faixa cobre também o `padding` de
     * quem rola, que o `sticky` não ocupa: `--form-veil-inset` é esse padding,
     * e `--form-veil` a cor. O `Modal` e o `SidePanel` declaram os dois; na
     * página valem zero e `fundo`.
     */
    return (
      <div className="sticky bottom-[calc(-1*var(--form-veil-inset,0px))] z-10 bg-[var(--form-veil,var(--color-fundo))] pt-3 pb-[var(--form-veil-inset,0px)]">
        {footer}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-borda pt-3.5',
        hasRequired ? 'justify-between' : 'justify-end',
        className,
      )}
    >
      {hasRequired && (
        <p className="flex max-w-sm items-start gap-1.5 text-xs text-texto-suave">
          <Icon nome="informacao" size={16} className="mt-px shrink-0" />
          <span>
            Campos marcados com <span className="font-semibold text-erro">*</span> são
            obrigatórios; os demais podem ficar em branco.
          </span>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export interface FormProps extends FormHTMLAttributes<HTMLFormElement> {
  children?: ReactNode;
  /**
   * O formulário LONGO: trilho de etapas ao lado, seção com ícone e selo de
   * preenchimento, e rodapé fixo com o que falta.
   *
   * Opcional e desligado por padrão — em 25/09/2026 só o sinistro o usa. Um
   * formulário de dois blocos com trilho é ruído; a pergunta para ligar é se
   * quem preenche se perde, e não se o formulário "fica mais bonito".
   *
   * Com UMA seção só não há trilho nem rodapé fixo: fica o cabeçalho com ícone
   * e selo, e o rodapé que diz o que falta — é a forma do cadastro do terceiro
   * e do veículo dele, que são um bloco cada.
   */
  steps?: boolean;
  /**
   * O formulário mora DENTRO de outro formulário, e por isso não é um `<form>`.
   *
   * `<form>` dentro de `<form>` é HTML inválido: o navegador descarta o de
   * dentro, e o Enter num campo dele enviaria o de FORA, pela metade. É o caso
   * do cadastro do terceiro, que abre dentro do formulário do sinistro.
   *
   * Aqui o bloco é um `<div>` que faz o que o `<form>` faria: Enter num campo
   * de texto chama `onSubmit`, e o clique num botão `type="submit"` também — e
   * os dois param ali, antes de chegar ao formulário de fora. O Enter num
   * `<textarea>` continua sendo quebra de linha. `onSubmit` recebe o evento do
   * teclado ou do clique; `preventDefault` nele funciona igual.
   *
   * Era o `FormBlock` do módulo de sinistros até 25/09/2026, e mora aqui
   * porque o `Form` precisa ser os dois para o cadastro do terceiro ter a
   * mesma forma do sinistro.
   */
  nested?: boolean;
}

type SubmitEvent = Parameters<
  NonNullable<FormHTMLAttributes<HTMLFormElement>['onSubmit']>
>[0];

/**
 * O `<form>` da casa: a pilha dos blocos, e nada além disso.
 *
 * **Ele É o `<form>`**, e não uma caixa com um formulário dentro. Com isso o
 * `Enter` em qualquer campo envia — é o que o `Filters` já faz com o painel
 * dele, e é o gesto que quem preenche formulário o dia inteiro usa sem pensar.
 *
 * Ele não desenha casca nenhuma: quem tem borda, fundo e sombra é o
 * `FormSection`. Um alerta de erro, um aviso de regra que não existe, o próprio
 * `FormFooter` — tudo isso entra como filho direto e fica **entre** os cartões,
 * que é onde essas coisas pertencem.
 *
 * ## Ele conta os blocos, e a conta é o que o cabeçalho de cada um mostra
 *
 * O `FormSection` não recebe "sou o terceiro de quatro": ele pergunta. A conta
 * sai da posição entre os filhos, então remover um bloco do meio renumera os
 * outros sozinho — e um formulário de um bloco não mostra número nenhum.
 *
 * O que é filho e **não** é seção não entra na conta: o alerta de erro no topo
 * não é o bloco 01.
 */
export function Form({
  children,
  className,
  steps = false,
  nested = false,
  onSubmit,
  ...props
}: FormProps) {
  /*
   * A conta das marcas de obrigatório, e não um booleano: dois campos
   * obrigatórios que aparecem e somem por permissão zeram na ordem em que
   * saírem, e um booleano apagaria o aviso ao primeiro que saísse.
   */
  const [marks, setMarks] = useState(0);
  const announce = useCallback((on: boolean) => {
    setMarks((count) => count + (on ? 1 : -1));
  }, []);

  const [attempted, setAttempted] = useState(false);
  const formId = useId();

  const items = Children.toArray(children);
  const sections = items.filter(isSection);
  const total = sections.length;

  /* As etapas saem das PROPS das seções — o `Form` já as percorre para
   * numerar, e nenhuma seção precisa se registrar em lugar nenhum. */
  const stepItems: StepItem[] = sections.map((section, index) => {
    const props = section.props as FormSectionProps;
    return {
      id: `${formId}-etapa-${index + 1}`,
      title: props.title,
      status: props.status,
    };
  });
  const remaining = stepItems.reduce(
    (sum, i) => sum + Math.max(0, (i.status?.total ?? 0) - (i.status?.done ?? 0)),
    0,
  );

  const goToNext = () => {
    const next = stepItems.find((i) => !isComplete(i.status));
    if (next) goTo(next.id);
  };

  const rail = steps && total > 1;

  const submit = (e: SubmitEvent) => {
    onSubmit?.(e);
    if (!steps) return;
    /* Tentar enviar com falta é o momento em que "ainda não" vira "falta": a
     * etapa incompleta fica vermelha, e a tela leva até a primeira. */
    setAttempted(true);
    if (remaining > 0) goToNext();
  };

  /* Só no `nested`. O evento vai para `onSubmit` como veio: quem chama só usa
   * o `preventDefault`, e ele existe nos dois. */
  const asSubmit = (e: KeyboardEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>) =>
    submit(e as unknown as SubmitEvent);

  const onNestedKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    /* O campo que já tratou o Enter manda nele, como no `<form>` de verdade:
     * o CEP do `AddressField` usa o Enter para buscar, e sem esta linha o
     * cadastro da empresa salvava antes de o endereço chegar. */
    if (e.defaultPrevented) return;
    if ((e.target as HTMLElement).tagName !== 'INPUT') return;
    e.preventDefault();
    e.stopPropagation();
    asSubmit(e);
  };

  const onNestedClick = (e: MouseEvent<HTMLDivElement>) => {
    const button = (e.target as HTMLElement).closest('button[type="submit"]');
    /* O botão é DESTE bloco, e não de um aninhado mais fundo: a captura corre
     * de fora para dentro, e o de fora veria primeiro o clique do de dentro. */
    if (!button || button.closest('[data-form-nested]') !== e.currentTarget) return;
    e.preventDefault();
    asSubmit(e);
  };

  let ordinal = 0;
  const stack = items.map((child, index) => {
    if (!isSection(child)) return child;

    ordinal += 1;
    const position = {
      index: ordinal,
      total,
      ...(steps ? { id: stepItems[ordinal - 1]!.id } : {}),
    };

    return (
      <SectionPosition.Provider key={child.key ?? index} value={position}>
        {child}
      </SectionPosition.Provider>
    );
  });

  return (
    <RequiredMarks.Provider value={announce}>
      <HasRequired.Provider value={marks > 0}>
        {/* 20px entre blocos: o respiro do desenho de 18/09, e o dobro do que
            separa duas fileiras dentro de um bloco. É a diferença que faz a
            pilha ler como blocos. */}
        <Steps.Provider value={steps ? { attempted, remaining, goToNext, rail } : null}>
          {nested ? (
            <div
              data-form-nested=""
              onKeyDown={onNestedKey}
              onClickCapture={onNestedClick}
              className={cn('flex flex-col gap-5', className)}
            >
              {stack}
            </div>
          ) : (
            <form
              {...props}
              onSubmit={submit}
              className={cn(
                rail
                  ? 'grid items-start gap-5 lg:grid-cols-[13.5rem_minmax(0,1fr)]'
                  : 'flex flex-col gap-5',
                className,
              )}
            >
              {rail ? (
                <>
                  <StepsRail items={stepItems} attempted={attempted} />
                  <div className="flex min-w-0 flex-col gap-5">{stack}</div>
                </>
              ) : (
                stack
              )}
            </form>
          )}
        </Steps.Provider>
      </HasRequired.Provider>
    </RequiredMarks.Provider>
  );
}

/*
 * A seção é reconhecida pelo TIPO, não por uma prop nem por um nome de
 * componente: `child.type === FormSection` continua verdadeiro depois de
 * qualquer renomeação, e um `displayName` comparado por string não.
 *
 * O preço é conhecido e está declarado no docblock do `Form`: uma seção
 * embrulhada por um componente da tela não é contada, porque o filho ali é o
 * embrulho. Quem quiser a numeração devolve a seção, não o embrulho.
 */
function isSection(child: ReactNode): child is ReactElement {
  return isValidElement(child) && child.type === FormSection;
}
